/**
 * CLIKKER Product Engine
 * Asset: clikker-product-engine.js
 * Version: 1.2.2 — default-rich products + creator-friendly publish validation
 *
 * Load before clikker-product-workflows.js and clikker-profile.js.
 * This engine is workflow-agnostic and does not access Firebase directly.
 */
(function (window, document) {
  "use strict";

  var VERSION = "1.2.2";
  var registry = Object.create(null);
  var order = [];
  var events = Object.create(null);
  var fulfilmentBindings = typeof WeakMap === "function" ? new WeakMap() : null;

  var FIELD_TYPES = [
    "text","textarea","number","select","multiselect","toggle","checkbox",
    "date","time","datetime-local","email","tel","url","file","image",
    "currency","hidden"
  ];

  var SYSTEM_BUYER_FIELDS = [
    { key:"customer_name", label:"Your name", type:"text", required:true, system:true, locked:true },
    { key:"customer_email", label:"Email address", type:"email", required:true, system:true, locked:true }
  ];

  function clean(value) {
    return String(value == null ? "" : value).trim();
  }

  function clone(value) {
    if (value == null) return value;
    if (typeof structuredClone === "function") {
      try { return structuredClone(value); } catch (_) {}
    }
    try {
      return JSON.parse(JSON.stringify(value));
    } catch (_) {
      return value;
    }
  }

  function plain(value) {
    return Object.prototype.toString.call(value) === "[object Object]";
  }

  function merge(base, extra) {
    var out = plain(base) ? clone(base) : {};
    if (!plain(extra)) return out;
    Object.keys(extra).forEach(function (key) {
      out[key] = plain(out[key]) && plain(extra[key])
        ? merge(out[key], extra[key])
        : clone(extra[key]);
    });
    return out;
  }

  function normalizeType(value) {
    return clean(value)
      .toLowerCase()
      .replace(/[\s-]+/g, "_")
      .replace(/[^a-z0-9_]/g, "")
      .replace(/^_+|_+$/g, "");
  }

  function normalizeKey(value) {
    return clean(value)
      .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 64);
  }

  function canonicalOrderField(value) {
    return normalizeKey(value);
  }

  function isPersistedFileValue(value) {
    if (!value) return false;
    if (typeof File !== "undefined" && value instanceof File) return true;
    if (typeof Blob !== "undefined" && value instanceof Blob) return true;
    if (typeof value === "string") return clean(value) !== "";
    if (!plain(value)) return false;
    return Boolean(
      value.pending === true ||
      clean(value.downloadURL || value.download_url || value.url) ||
      clean(value.storagePath || value.storage_path || value.path)
    );
  }

  function escapeHTML(value) {
    return String(value == null ? "" : value).replace(/[&<>"']/g, function (c) {
      return {"&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#039;"}[c];
    });
  }

  function titleCase(value) {
    return clean(value).replace(/[_-]+/g, " ").replace(/\b\w/g, function (c) {
      return c.toUpperCase();
    });
  }

  function array(value) {
    return Array.isArray(value) ? value : [];
  }

  function number(value, fallback) {
    var parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function empty(value) {
    if (value == null) return true;
    if (typeof value === "string") return clean(value) === "";
    if (Array.isArray(value)) return value.length === 0;
    return false;
  }

  function uniqueFields(fields) {
    var seen = Object.create(null);
    return array(fields).filter(function (field) {
      if (!field || !field.key || seen[field.key]) return false;
      seen[field.key] = true;
      return true;
    });
  }

  function normalizeOption(option) {
    if (plain(option)) {
      return {
        label: clean(option.label || option.value),
        value: clean(option.value == null ? option.label : option.value),
        disabled: option.disabled === true
      };
    }
    return { label:clean(option), value:clean(option), disabled:false };
  }

  function normalizeField(field, mode) {
    field = plain(field) ? clone(field) : {};
    var type = clean(field.input || field.type || field.component || "text").toLowerCase();

    if (type === "switch") type = "toggle";
    if (type === "phone") type = "tel";
    if (type === "datetime") type = "datetime-local";
    if (FIELD_TYPES.indexOf(type) < 0) type = "text";

    var key = normalizeKey(field.key || field.name || field.label);
    if (!key) throw new Error("Every product field needs a valid key.");

    return {
      key:key,
      label:clean(field.label || titleCase(key)),
      type:type,
      input:type,
      mode:mode || "fulfilment",
      required:field.required === true,
      /*
        publishRequired controls whether an empty fulfilment field blocks the
        creator from publishing. When omitted it keeps the old behaviour.
        Workflows can now keep a field prominent/required in a particular form
        while making it non-blocking for creator publishing when appropriate.
      */
      publishRequired:field.publishRequired === undefined
        ? field.required === true
        : field.publishRequired === true,
      recommended:field.recommended === true,
      system:field.system === true,
      locked:field.locked === true,
      enabled:field.enabled !== false,
      placeholder:clean(field.placeholder),
      helpText:clean(field.helpText || field.help),
      defaultValue:field.defaultValue !== undefined
        ? clone(field.defaultValue)
        : (type === "toggle" || type === "checkbox" ? false : ""),
      options:array(field.options).map(normalizeOption),
      accept:clean(field.accept),
      min:field.min === undefined || field.min === "" ? null : number(field.min, null),
      max:field.max === undefined || field.max === "" ? null : number(field.max, null),
      step:field.step === undefined || field.step === "" ? null : number(field.step, null),
      minLength:field.minLength === undefined ? null : number(field.minLength, null),
      maxLength:field.maxLength === undefined ? null : number(field.maxLength, null),
      pattern:clean(field.pattern),
      rows:number(field.rows, 4),
      multiple:field.multiple === true || type === "multiselect",
      visibleWhen:plain(field.visibleWhen) ? clone(field.visibleWhen) : null,
      validation:plain(field.validation) ? clone(field.validation) : {},
      metadata:plain(field.metadata) ? clone(field.metadata) : {}
    };
  }

  function normalizeBuyerField(field) {
    var output = normalizeField(field, "buyer");
    delete output.input;
    return output;
  }

  function normalizeWorkflow(definition) {
    if (!plain(definition)) throw new Error("Workflow definition must be an object.");

    var type = normalizeType(definition.type || definition.id);
    if (!type) throw new Error("Workflow definition requires a type.");

    var meta = merge({
      icon:"✨",
      label:titleCase(type),
      category:"Other",
      description:"",
      status:"active",
      sortOrder:999,
      schemaVersion:1,
      allowMultipleProducts:true
    }, definition.meta || {});

    meta.label = clean(meta.label || titleCase(type));
    meta.icon = clean(meta.icon || "✨");
    meta.status = clean(meta.status || "active").toLowerCase();
    meta.sortOrder = number(meta.sortOrder, 999);
    meta.schemaVersion = Math.max(1, number(meta.schemaVersion, 1));
    meta.allowMultipleProducts = meta.allowMultipleProducts !== false;

    var defaults = merge({
      title:meta.label,
      description:clean(meta.description),
      price:0,
      currency:"INR",
      status:"draft",
      visibility:"private"
    }, definition.defaults || {});

    var fulfilmentFields = uniqueFields(array(
      definition.fulfilmentFields ||
      definition.fulfillmentFields ||
      definition.fulfilment
    ).map(function (field) {
      return normalizeField(field, "fulfilment");
    }));

    var buyerFields = uniqueFields(array(definition.buyerFields).map(normalizeBuyerField));

    if (definition.includeSystemBuyerFields !== false) {
      buyerFields = uniqueFields(
        SYSTEM_BUYER_FIELDS.map(normalizeBuyerField).concat(buyerFields)
      );
    }

    return {
      type:type,
      meta:meta,
      defaults:defaults,
      fulfilmentFields:fulfilmentFields,
      buyerFields:buyerFields,
      orderWorkflow:(function () {
        var orderWorkflow = merge({
          type:"manual_delivery",
          deliveryMode:"manual",
          supportsText:true,
          supportsFile:true,
          supportsLink:true,
          requiresSchedule:false,
          requiresApproval:false,
          completionFields:[],
          canonicalCompletionFields:[]
        }, definition.orderWorkflow || definition.workflow || {});

        orderWorkflow.completionFields = array(orderWorkflow.completionFields).map(clean).filter(Boolean);
        orderWorkflow.canonicalCompletionFields = array(
          orderWorkflow.canonicalCompletionFields && orderWorkflow.canonicalCompletionFields.length
            ? orderWorkflow.canonicalCompletionFields
            : orderWorkflow.completionFields
        ).map(canonicalOrderField).filter(Boolean);

        return orderWorkflow;
      })(),
      validation:merge({
        minPrice:1,
        maxPrice:null,
        maxBuyerFields:10
      }, definition.validation || {}),
      presets:array(definition.presets).map(clone),
      hooks:plain(definition.hooks) ? definition.hooks : {}
    };
  }

  function emit(name, payload) {
    array(events[name]).forEach(function (listener) {
      try { listener(payload); }
      catch (error) { console.error("[Clikker Product Engine]", error); }
    });
  }

  function on(name, listener) {
    if (typeof listener !== "function") return function () {};
    events[name] = events[name] || [];
    events[name].push(listener);
    return function () {
      events[name] = array(events[name]).filter(function (current) {
        return current !== listener;
      });
    };
  }

  function registerWorkflow(definition) {
    var workflow = normalizeWorkflow(definition);
    var replacing = Boolean(registry[workflow.type]);

    registry[workflow.type] = workflow;
    if (!replacing) order.push(workflow.type);

    order.sort(function (a, b) {
      return registry[a].meta.sortOrder - registry[b].meta.sortOrder ||
        registry[a].meta.label.localeCompare(registry[b].meta.label);
    });

    emit(replacing ? "workflow:updated" : "workflow:registered", clone(workflow));
    return clone(workflow);
  }

  function registerWorkflows(definitions) {
    return array(definitions).map(registerWorkflow);
  }

  function getWorkflow(type) {
    type = normalizeType(type);
    return registry[type] ? clone(registry[type]) : null;
  }

  function hasWorkflow(type) {
    return Boolean(registry[normalizeType(type)]);
  }

  function listWorkflows(options) {
    options = plain(options) ? options : {};
    return order.map(function (type) {
      return registry[type];
    }).filter(function (workflow) {
      if (options.includeInactive === true) return true;
      return workflow.meta.status === "active";
    }).filter(function (workflow) {
      if (!options.category) return true;
      return clean(workflow.meta.category).toLowerCase() ===
        clean(options.category).toLowerCase();
    }).map(clone);
  }

  function unregisterWorkflow(type) {
    type = normalizeType(type);
    if (!registry[type]) return false;
    delete registry[type];
    order = order.filter(function (item) { return item !== type; });
    emit("workflow:removed", { type:type });
    return true;
  }

  function productTypeOf(product) {
    return normalizeType(product && (
      product.workflowType ||
      product.fulfilmentType ||
      product.fulfillmentType ||
      product.type
    ));
  }

  function defaultFulfilment(workflow) {
    var values = {};
    array(workflow.fulfilmentFields).forEach(function (field) {
      values[field.key] = clone(field.defaultValue);
    });
    return values;
  }

  function createProductDraft(type, overrides) {
    var workflow = registry[normalizeType(type)];
    if (!workflow) throw new Error("Unknown product workflow: " + clean(type));

    /*
      Start with the complete workflow defaults object so a workflow can ship a
      ready-to-publish product with default short description, instructions,
      pricing model, badge, policies and any future creator fields.
    */
    var draft = merge(
      merge(workflow.defaults, {
        id:"",
        creatorId:"",
        ownerId:"",
        storeId:"",
        workflowType:workflow.type,
        fulfilmentType:workflow.type,
        type:workflow.type,
        schemaVersion:workflow.meta.schemaVersion,
        title:workflow.defaults.title,
        description:workflow.defaults.description,
        price:workflow.defaults.price,
        currency:workflow.defaults.currency || "INR",
        coverImage:"",
        status:workflow.defaults.status || "draft",
        visibility:workflow.defaults.visibility || "private",
        fulfilment:defaultFulfilment(workflow),
        buyerFields:clone(workflow.buyerFields),
        terms:plain(workflow.defaults.terms) ? clone(workflow.defaults.terms) : {},
        metadata:plain(workflow.defaults.metadata) ? clone(workflow.defaults.metadata) : {}
      }),
      overrides || {}
    );

    return normalizeProduct(draft);
  }

  function normalizeProduct(product) {
    product = plain(product) ? clone(product) : {};
    var type = productTypeOf(product);
    var workflow = registry[type];
    if (!workflow) return product;

    var base = merge(workflow.defaults, {
      workflowType:type,
      fulfilmentType:type,
      type:type,
      schemaVersion:workflow.meta.schemaVersion,
      title:workflow.defaults.title,
      description:workflow.defaults.description,
      price:workflow.defaults.price,
      currency:workflow.defaults.currency || "INR",
      status:workflow.defaults.status || "draft",
      visibility:workflow.defaults.visibility || "private",
      fulfilment:defaultFulfilment(workflow),
      buyerFields:clone(workflow.buyerFields),
      terms:plain(workflow.defaults.terms) ? clone(workflow.defaults.terms) : {},
      metadata:plain(workflow.defaults.metadata) ? clone(workflow.defaults.metadata) : {}
    });

    var normalized = merge(base, product);
    normalized.workflowType = type;
    normalized.fulfilmentType = type;
    normalized.type = type;
    normalized.schemaVersion = Math.max(1, number(
      normalized.schemaVersion,
      workflow.meta.schemaVersion
    ));
    normalized.title = clean(normalized.title);
    normalized.description = clean(normalized.description);
    normalized.price = number(normalized.price, 0);
    normalized.currency = clean(normalized.currency || "INR").toUpperCase();
    normalized.status = clean(normalized.status || "draft").toLowerCase();
    normalized.visibility = clean(normalized.visibility || "private").toLowerCase();
    normalized.fulfilment = merge(
      defaultFulfilment(workflow),
      normalized.fulfilment || normalized.fulfillment || normalized.workflowSettings || {}
    );

    var buyerFields = uniqueFields(array(normalized.buyerFields).map(normalizeBuyerField));
    var protectedFields = workflow.buyerFields.filter(function (field) {
      return field.system && field.locked;
    });
    normalized.buyerFields = uniqueFields(protectedFields.concat(buyerFields));
    normalized.terms = plain(normalized.terms) ? normalized.terms : {};
    normalized.metadata = plain(normalized.metadata) ? normalized.metadata : {};

    delete normalized.fulfillment;
    delete normalized.workflowSettings;
    return normalized;
  }

  function visible(field, values) {
    if (!field.visibleWhen) return true;
    var current = values[field.visibleWhen.field];

    if (Object.prototype.hasOwnProperty.call(field.visibleWhen, "equals")) {
      return current === field.visibleWhen.equals;
    }
    if (Object.prototype.hasOwnProperty.call(field.visibleWhen, "notEquals")) {
      return current !== field.visibleWhen.notEquals;
    }
    if (Array.isArray(field.visibleWhen.in)) {
      return field.visibleWhen.in.indexOf(current) >= 0;
    }
    if (Array.isArray(field.visibleWhen.notIn)) {
      return field.visibleWhen.notIn.indexOf(current) < 0;
    }
    return true;
  }

  function resolve(target) {
    if (!target) return null;
    return typeof target === "string" ? document.querySelector(target) : target;
  }

  function renderField(field, value, options) {
    options = options || {};
    var id = clean(options.idPrefix || "clikkerField") + "_" + field.key;
    var name = clean(options.namePrefix)
      ? options.namePrefix + "[" + field.key + "]"
      : field.key;
    var attrs = [
      'id="' + escapeHTML(id) + '"',
      'name="' + escapeHTML(name) + '"',
      'data-clikker-field="' + escapeHTML(field.key) + '"',
      'data-clikker-field-type="' + escapeHTML(field.type) + '"'
    ];

    if (field.required) attrs.push("required");
    if (field.placeholder) attrs.push('placeholder="' + escapeHTML(field.placeholder) + '"');
    if (field.min != null) attrs.push('min="' + field.min + '"');
    if (field.max != null) attrs.push('max="' + field.max + '"');
    if (field.step != null) attrs.push('step="' + field.step + '"');
    if (field.minLength != null) attrs.push('minlength="' + field.minLength + '"');
    if (field.maxLength != null) attrs.push('maxlength="' + field.maxLength + '"');
    if (field.pattern) attrs.push('pattern="' + escapeHTML(field.pattern) + '"');
    if (field.accept) attrs.push('accept="' + escapeHTML(field.accept) + '"');
    if (field.multiple) attrs.push("multiple");

    var required = field.required ? ' <span aria-hidden="true">*</span>' : "";
    var current = value == null ? "" : value;
    var control = "";

    if (field.type === "textarea") {
      control = '<textarea class="ccx-textarea" rows="' + field.rows + '" ' +
        attrs.join(" ") + ">" + escapeHTML(current) + "</textarea>";
    } else if (field.type === "select" || field.type === "multiselect") {
      var selected = Array.isArray(current) ? current.map(String) : [String(current)];

      control = '<select class="ccx-select" ' + attrs.join(" ") + ">" +
        (field.required && !field.multiple ? '<option value="">Choose an option</option>' : "") +
        field.options.map(function (option) {
          return '<option value="' + escapeHTML(option.value) + '"' +
            (selected.indexOf(String(option.value)) >= 0 ? " selected" : "") +
            (option.disabled ? " disabled" : "") + ">" +
            escapeHTML(option.label) + "</option>";
        }).join("") + "</select>";
    } else if (field.type === "toggle" || field.type === "checkbox") {
      control = '<label class="ccx-section-toggle"><span><strong>' +
        escapeHTML(field.label) + "</strong>" +
        (field.helpText ? "<span>" + escapeHTML(field.helpText) + "</span>" : "") +
        '</span><input type="checkbox" ' + attrs.join(" ") +
        (current === true || current === "true" || current === 1 ? " checked" : "") +
        "></label>";
    } else if (field.type === "hidden") {
      control = '<input type="hidden" ' + attrs.join(" ") +
        ' value="' + escapeHTML(current) + '">';
    } else {
      var htmlType = field.type === "currency" ? "number" :
        (field.type === "image" ? "file" : field.type);

      if (field.type === "currency" && field.step == null) {
        attrs.push('step="0.01"', 'min="0"');
      }

      control = '<input class="ccx-input" type="' + escapeHTML(htmlType) + '" ' +
        attrs.join(" ") +
        (field.type === "file" || field.type === "image"
          ? ""
          : ' value="' + escapeHTML(current) + '"') + ">";
    }

    if (field.type === "toggle" || field.type === "checkbox" || field.type === "hidden") {
      return '<div class="ccx-field" data-clikker-field-wrap="' + escapeHTML(field.key) + '">' +
        control + '<small class="ccx-field-error" data-clikker-field-error="' +
        field.key + '" hidden></small></div>';
    }

    return '<div class="ccx-field" data-clikker-field-wrap="' + escapeHTML(field.key) + '">' +
      '<label for="' + escapeHTML(id) + '">' + escapeHTML(field.label) + required + "</label>" +
      control +
      (field.helpText ? "<small>" + escapeHTML(field.helpText) + "</small>" : "") +
      '<small class="ccx-field-error" data-clikker-field-error="' +
      field.key + '" hidden></small></div>';
  }

  function collectFieldValues(container) {
    container = resolve(container);
    var values = {};
    if (!container) return values;

    container.querySelectorAll("[data-clikker-field]").forEach(function (control) {
      var key = normalizeKey(control.dataset.clikkerField);
      var type = clean(control.dataset.clikkerFieldType);
      if (!key) return;

      if (type === "toggle" || type === "checkbox") {
        values[key] = Boolean(control.checked);
      } else if (type === "number" || type === "currency") {
        values[key] = control.value === "" ? "" : number(control.value, 0);
      } else if (type === "multiselect") {
        values[key] = Array.prototype.slice.call(control.selectedOptions || [])
          .map(function (option) { return option.value; });
      } else if (type === "file" || type === "image") {
        if (!control.files || !control.files.length) {
          values[key] = control.multiple ? [] : null;
        } else {
          values[key] = control.multiple
            ? Array.prototype.slice.call(control.files)
            : control.files[0];
        }
      } else {
        values[key] = control.value;
      }
    });

    return values;
  }

  function refreshVisibility(container, fields) {
    var values = collectFieldValues(container);
    fields.forEach(function (field) {
      var wrapper = container.querySelector('[data-clikker-field-wrap="' + field.key + '"]');
      if (wrapper) wrapper.hidden = !visible(field, values);
    });
  }

  function renderFulfilmentFields(container, type, values, options) {
    container = resolve(container);
    if (!container) throw new Error("Fulfilment container not found.");

    var workflow = registry[normalizeType(type)];
    if (!workflow) {
      container.innerHTML = '<div class="ccx-empty"><h3>Workflow unavailable</h3>' +
        "<p>This product workflow has not been registered.</p></div>";
      return [];
    }

    values = merge(defaultFulfilment(workflow), values || {});
    var fields = workflow.fulfilmentFields.filter(function (field) {
      return field.enabled !== false;
    });

    container.innerHTML = fields.length
      ? '<div class="ccx-grid ccx-grid--2">' + fields.map(function (field) {
          var fieldOptions = merge({
            idPrefix:"clikkerFulfilment",
            namePrefix:"fulfilment"
          }, options || {});
          return renderField(field, values[field.key], fieldOptions);
        }).join("") + "</div>"
      : '<div class="ccx-empty"><h3>No additional setup required</h3>' +
        "<p>This workflow uses only common product information.</p></div>";

    container.dataset.workflowType = workflow.type;

    if (fulfilmentBindings) {
      var previousBinding = fulfilmentBindings.get(container);
      if (previousBinding) {
        container.removeEventListener("input", previousBinding);
        container.removeEventListener("change", previousBinding);
      }
    }

    var refresh = function () { refreshVisibility(container, fields); };
    container.addEventListener("input", refresh);
    container.addEventListener("change", refresh);
    if (fulfilmentBindings) fulfilmentBindings.set(container, refresh);
    refresh();

    emit("fulfilment:rendered", { type:workflow.type, container:container });
    return clone(fields);
  }

  function renderBuyerFields(container, fields, options) {
    container = resolve(container);
    if (!container) throw new Error("Buyer field container not found.");
    options = options || {};

    var normalized = uniqueFields(array(fields).map(normalizeBuyerField))
      .filter(function (field) { return field.enabled !== false; });

    container.innerHTML = normalized.length
      ? '<div class="ccx-form-builder">' + normalized.map(function (field, index) {
          var removable = options.editable !== false && !field.system && !field.locked;
          return '<article class="ccx-form-field-card" data-buyer-field-index="' +
            index + '" data-buyer-field-key="' + field.key + '">' +
            '<span class="ccx-form-field-card__handle" aria-hidden="true">⋮⋮</span>' +
            "<div><h4>" + escapeHTML(field.label) + (field.required ? " *" : "") +
            (field.system ? '<span class="ccx-system-field">System</span>' : "") +
            "</h4><p>" + escapeHTML(titleCase(field.type)) + "</p>" +
            '<script type="application/json" data-buyer-field-json>' +
            JSON.stringify(field).replace(/</g, "\\u003c") + "</script></div>" +
            '<div class="ccx-form-field-card__actions">' +
            (options.editable === false ? "" :
              '<button class="ccx-icon-btn" type="button" data-edit-buyer-field="' +
              index + '" aria-label="Edit field">✎</button>') +
            (removable ? '<button class="ccx-icon-btn" type="button" data-remove-buyer-field="' +
              index + '" aria-label="Remove field">×</button>' : "") +
            "</div></article>";
        }).join("") + "</div>"
      : '<div class="ccx-empty"><h3>No buyer questions</h3>' +
        "<p>Add questions so buyers can provide the required information.</p></div>";

    return clone(normalized);
  }

  function collectBuyerFields(container) {
    container = resolve(container);
    if (!container) return [];

    return Array.prototype.slice.call(
      container.querySelectorAll("[data-buyer-field-json]")
    ).map(function (node) {
      try { return normalizeBuyerField(JSON.parse(node.textContent || "{}")); }
      catch (_) { return null; }
    }).filter(Boolean);
  }

  function renderWorkflowChoices(container, options) {
    container = resolve(container);
    if (!container) throw new Error("Workflow choice container not found.");

    options = options || {};
    var selectedType = normalizeType(options.selectedType);
    var workflows = listWorkflows({
      includeInactive:options.includeInactive === true,
      category:options.category
    });

    container.innerHTML = workflows.length
      ? workflows.map(function (workflow) {
          var selected = workflow.type === selectedType;
          return '<button type="button" class="ccx-onboarding-product' +
            (selected ? " is-selected" : "") + '" data-clikker-workflow="' +
            workflow.type + '" role="radio" aria-checked="' +
            (selected ? "true" : "false") + '">' +
            '<span class="ccx-onboarding-product__icon">' +
            escapeHTML(workflow.meta.icon) + "</span><strong>" +
            escapeHTML(workflow.meta.label) + "</strong><small>" +
            escapeHTML(workflow.meta.description ||
              "Create your own offer with this workflow.") +
            "</small></button>";
        }).join("")
      : '<div class="ccx-empty"><h3>No workflows available</h3></div>';

    container.setAttribute("role", "radiogroup");

    container.onclick = function (event) {
      var button = event.target.closest("[data-clikker-workflow]");
      if (!button || !container.contains(button)) return;
      var type = normalizeType(button.dataset.clikkerWorkflow);

      container.querySelectorAll("[data-clikker-workflow]").forEach(function (item) {
        var active = normalizeType(item.dataset.clikkerWorkflow) === type;
        item.classList.toggle("is-selected", active);
        item.setAttribute("aria-checked", active ? "true" : "false");
      });

      emit("workflow:selected", { type:type, workflow:getWorkflow(type), source:button });
      if (typeof options.onSelect === "function") {
        options.onSelect(type, getWorkflow(type), button);
      }
    };

    return workflows;
  }

  function validateField(field, value, values) {
    var errors = [];
    if (!visible(field, values || {})) return errors;

    if (field.required && empty(value)) {
      errors.push(field.label + " is required.");
      return errors;
    }
    if (empty(value)) return errors;

    var text = Array.isArray(value) ? value.join(",") : clean(value);

    if (field.minLength != null && text.length < field.minLength) {
      errors.push(field.label + " must contain at least " + field.minLength + " characters.");
    }
    if (field.maxLength != null && text.length > field.maxLength) {
      errors.push(field.label + " must not exceed " + field.maxLength + " characters.");
    }
    if (field.type === "number" || field.type === "currency") {
      var numeric = Number(value);
      if (!Number.isFinite(numeric)) errors.push(field.label + " must be a valid number.");
      if (field.min != null && numeric < field.min) {
        errors.push(field.label + " must be at least " + field.min + ".");
      }
      if (field.max != null && numeric > field.max) {
        errors.push(field.label + " must not exceed " + field.max + ".");
      }
    }
    if (field.type === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text)) {
      errors.push(field.label + " must be a valid email address.");
    }
    if (field.type === "url") {
      try {
        var parsedURL = new URL(text);
        if (["http:", "https:"].indexOf(parsedURL.protocol) < 0) {
          throw new Error("Unsupported URL protocol");
        }
      } catch (_) {
        errors.push(field.label + " must be a valid HTTP or HTTPS URL.");
      }
    }
    if ((field.type === "file" || field.type === "image") && !isPersistedFileValue(value)) {
      errors.push(field.label + " must contain a valid uploaded or selected file.");
    }
    if (field.pattern) {
      try {
        if (!new RegExp(field.pattern).test(text)) {
          errors.push(field.label + " has an invalid format.");
        }
      } catch (_) {}
    }
    return errors;
  }

  function addHookResult(errors, fieldErrors, result) {
    if (!result) return;
    if (typeof result === "string") errors.push(result);
    if (Array.isArray(result)) errors.push.apply(errors, result);
    if (plain(result)) {
      if (Array.isArray(result.errors)) errors.push.apply(errors, result.errors);
      if (plain(result.fieldErrors)) {
        Object.keys(result.fieldErrors).forEach(function (key) {
          fieldErrors[key] = array(fieldErrors[key]).concat(array(result.fieldErrors[key]));
          errors.push.apply(errors, array(result.fieldErrors[key]));
        });
      }
    }
  }

  function validateFulfilment(type, values, options) {
    options = options || {};
    var workflow = registry[normalizeType(type)];

    if (!workflow) {
      return { valid:false, errors:["The selected workflow is unavailable."], fieldErrors:{} };
    }

    values = merge(defaultFulfilment(workflow), values || {});
    var errors = [];
    var fieldErrors = {};

    workflow.fulfilmentFields.forEach(function (field) {
      /*
        For creator publishing, only publishRequired fields block completion.
        Conditional visibility is still respected by validateField().
      */
      var validationField = clone(field);
      validationField.required = field.publishRequired === true;

      var current = validateField(
        validationField,
        values[field.key],
        values
      );

      if (current.length) {
        fieldErrors[field.key] = current;
        errors.push.apply(errors, current);
      }
    });

    if (typeof workflow.hooks.validateFulfilment === "function") {
      addHookResult(
        errors,
        fieldErrors,
        workflow.hooks.validateFulfilment(clone(values), options.context || {})
      );
    }

    return { valid:errors.length === 0, errors:errors, fieldErrors:fieldErrors, values:values };
  }

  function validateBuyerFields(fields, options) {
    options = options || {};

    /*
      Validate the full normalized input BEFORE de-duplication.
      v1.2.0 used uniqueFields() first, so duplicate keys could never
      reach the duplicate-key validation below.
    */
    var source = array(fields).map(normalizeBuyerField);
    var normalized = uniqueFields(source);
    var errors = [];
    var fieldErrors = {};
    var maxFields = number(options.maxFields, 10);

    if (source.length > maxFields) {
      errors.push("A product can contain a maximum of " + maxFields + " buyer fields.");
    }

    var keys = Object.create(null);
    source.forEach(function (field, index) {
      var current = [];

      if (keys[field.key]) {
        current.push('Buyer field key "' + field.key + '" is duplicated.');
      }
      keys[field.key] = true;

      if (!field.label) {
        current.push("Buyer field " + (index + 1) + " needs a label.");
      }

      if (
        (field.type === "select" || field.type === "multiselect") &&
        !field.options.length
      ) {
        current.push(field.label + " needs at least one option.");
      }

      if (current.length) {
        var errorKey = field.key || String(index);
        fieldErrors[errorKey] = (fieldErrors[errorKey] || []).concat(current);
        errors.push.apply(errors, current);
      }
    });

    ["customer_name","customer_email"].forEach(function (key) {
      var found = source.some(function (field) {
        return field.key === key && field.required && field.system;
      });

      if (!found && options.requireSystemFields !== false) {
        errors.push(titleCase(key) + " must remain in the buyer form.");
      }
    });

    return {
      valid:errors.length === 0,
      errors:errors,
      fieldErrors:fieldErrors,
      fields:normalized
    };
  }

  function validateProduct(product, options) {
    options = options || {};
    var sourceProduct = plain(product) ? clone(product) : {};
    var normalized = normalizeProduct(sourceProduct);
    var type = productTypeOf(normalized);
    var workflow = registry[type];
    var errors = [];
    var fieldErrors = {};

    function add(key, message) {
      fieldErrors[key] = fieldErrors[key] || [];
      fieldErrors[key].push(message);
      errors.push(message);
    }

    if (!workflow) {
      add("workflowType", "Choose a valid product workflow.");
      return { valid:false, errors:errors, fieldErrors:fieldErrors, product:normalized };
    }

    if (!normalized.title) add("title", "Product title is required.");
    if (normalized.title && normalized.title.length > 90) {
      add("title", "Product title must not exceed 90 characters.");
    }
    if (!normalized.description) add("description", "Product description is required.");

    var minPrice = number(workflow.validation.minPrice, 1);
    var maxPrice = workflow.validation.maxPrice == null
      ? null : number(workflow.validation.maxPrice, null);

    if (!Number.isFinite(normalized.price) || normalized.price < minPrice) {
      add("price", "Price must be at least " + minPrice + " " + normalized.currency + ".");
    }
    if (maxPrice != null && normalized.price > maxPrice) {
      add("price", "Price must not exceed " + maxPrice + " " + normalized.currency + ".");
    }
    if (!/^[A-Z]{3}$/.test(normalized.currency)) {
      add("currency", "Choose a valid three-letter currency.");
    }

    var fulfilment = validateFulfilment(type, normalized.fulfilment, options);
    errors.push.apply(errors, fulfilment.errors);
    Object.keys(fulfilment.fieldErrors).forEach(function (key) {
      fieldErrors["fulfilment." + key] = fulfilment.fieldErrors[key];
    });

    var buyer = validateBuyerFields(
      Array.isArray(sourceProduct.buyerFields)
        ? sourceProduct.buyerFields
        : normalized.buyerFields,
      {
        maxFields:workflow.validation.maxBuyerFields,
        requireSystemFields:true
      }
    );
    errors.push.apply(errors, buyer.errors);
    Object.keys(buyer.fieldErrors).forEach(function (key) {
      fieldErrors["buyerFields." + key] = buyer.fieldErrors[key];
    });

    if (typeof workflow.hooks.validateProduct === "function") {
      addHookResult(
        errors,
        fieldErrors,
        workflow.hooks.validateProduct(clone(normalized), options.context || {})
      );
    }

    return {
      valid:errors.length === 0,
      errors:errors,
      fieldErrors:fieldErrors,
      product:normalized,
      workflow:clone(workflow)
    };
  }

  function readiness(product, options) {
    var result = validateProduct(product, options);
    var productData = result.product || normalizeProduct(product);
    var type = productTypeOf(productData);
    var workflow = registry[type];
    var checks = [];

    function check(key, label, complete, required) {
      checks.push({
        key:key,
        label:label,
        complete:Boolean(complete),
        required:required !== false
      });
    }

    var readinessMinPrice = workflow
      ? number(workflow.validation.minPrice, 1)
      : 1;
    var readinessMaxPrice = workflow && workflow.validation.maxPrice != null
      ? number(workflow.validation.maxPrice, null)
      : null;
    var readinessPrice = Number(productData.price);
    var readinessPriceValid =
      Number.isFinite(readinessPrice) &&
      readinessPrice >= readinessMinPrice &&
      (readinessMaxPrice == null || readinessPrice <= readinessMaxPrice);

    check("workflow", "Product workflow selected", Boolean(workflow));
    check("title", "Product title added", Boolean(productData.title));
    check("description", "Product description added", Boolean(productData.description));
    check("price", "Valid product price", readinessPriceValid);
    check("fulfilment", "Fulfilment settings completed",
      workflow ? validateFulfilment(type, productData.fulfilment, options).valid : false);
    check("buyerFields", "Buyer form is ready",
      workflow ? validateBuyerFields(productData.buyerFields, {
        maxFields:workflow.validation.maxBuyerFields,
        requireSystemFields:true
      }).valid : false);
    check("coverImage", "Product image added", Boolean(
      productData.coverImage || productData.coverImageURL || productData.imageURL
    ), false);

    var required = checks.filter(function (item) { return item.required; });
    var complete = required.filter(function (item) { return item.complete; }).length;

    var recommendations = workflow
      ? workflow.fulfilmentFields
          .filter(function (field) {
            return field.recommended === true && field.publishRequired !== true;
          })
          .filter(function (field) {
            var values = productData.fulfilment || {};
            return visible(field, values) && empty(values[field.key]);
          })
          .map(function (field) {
            return {
              key:field.key,
              label:field.label,
              complete:false,
              required:false
            };
          })
      : [];

    return {
      score:required.length ? Math.round(complete / required.length * 100) : 0,
      readyToPublish:result.valid,
      checks:checks,
      recommendations:recommendations,
      errors:result.errors,
      fieldErrors:result.fieldErrors
    };
  }

  function applyValidationErrors(container, result) {
    container = resolve(container);
    if (!container) return;

    container.querySelectorAll("[data-clikker-field-error]").forEach(function (node) {
      node.hidden = true;
      node.textContent = "";
    });
    container.querySelectorAll("[data-clikker-field]").forEach(function (control) {
      control.removeAttribute("aria-invalid");
    });

    if (!result || !plain(result.fieldErrors)) return;

    Object.keys(result.fieldErrors).forEach(function (rawKey) {
      var key = rawKey.replace(/^fulfilment\./, "");
      var error = container.querySelector('[data-clikker-field-error="' + key + '"]');
      var control = container.querySelector('[data-clikker-field="' + key + '"]');

      if (error) {
        error.textContent = array(result.fieldErrors[rawKey]).join(" ");
        error.hidden = false;
      }
      if (control) control.setAttribute("aria-invalid", "true");
    });
  }

  function collectFulfilmentValues(container, type) {
    var values = collectFieldValues(container);
    var workflow = registry[normalizeType(type)];
    if (!workflow) return values;

    workflow.fulfilmentFields.forEach(function (field) {
      if (!Object.prototype.hasOwnProperty.call(values, field.key)) {
        values[field.key] = clone(field.defaultValue);
      }
    });
    return values;
  }

  function buildProductPayload(product) {
    var result = validateProduct(product);
    if (!result.valid) {
      var error = new Error(result.errors[0] || "Product is not ready to save.");
      error.code = "clikker/invalid-product";
      error.validation = result;
      throw error;
    }

    var payload = clone(result.product);
    payload.workflowType = productTypeOf(payload);
    payload.fulfilmentType = payload.workflowType;
    payload.type = payload.workflowType;
    payload.schemaVersion = registry[payload.workflowType].meta.schemaVersion;
    delete payload.id;
    return payload;
  }

  function buildOrderSnapshot(product) {
    var normalized = normalizeProduct(product);
    var type = productTypeOf(normalized);
    var workflow = registry[type];
    if (!workflow) throw new Error("The product workflow is unavailable.");

    return {
      productId:clean(normalized.id),
      creatorId:clean(normalized.creatorId),
      ownerId:clean(normalized.ownerId),
      storeId:clean(normalized.storeId),
      workflowType:type,
      schemaVersion:normalized.schemaVersion,
      productSnapshot:{
        title:normalized.title,
        description:normalized.description,
        price:normalized.price,
        currency:normalized.currency,
        coverImage:normalized.coverImage || normalized.coverImageURL || normalized.imageURL || "",
        fulfilment:clone(normalized.fulfilment),
        buyerFields:clone(normalized.buyerFields),
        terms:clone(normalized.terms)
      },
      orderWorkflow:clone(workflow.orderWorkflow),
      paymentStatus:"pending",
      fulfilmentStatus:"new"
    };
  }

  function runHook(type, hookName, payload, context) {
    var workflow = registry[normalizeType(type)];
    if (!workflow || typeof workflow.hooks[hookName] !== "function") return payload;
    return workflow.hooks[hookName](clone(payload), context || {});
  }

  function upgradeProduct(product) {
    var normalized = normalizeProduct(product);
    var workflow = registry[productTypeOf(normalized)];
    if (!workflow) return normalized;

    if (
      normalized.schemaVersion < workflow.meta.schemaVersion &&
      typeof workflow.hooks.migrateProduct === "function"
    ) {
      normalized = normalizeProduct(
        workflow.hooks.migrateProduct(
          clone(normalized),
          normalized.schemaVersion,
          workflow.meta.schemaVersion
        ) || normalized
      );
    }

    normalized.schemaVersion = workflow.meta.schemaVersion;
    return normalized;
  }

  var api = {
    version:VERSION,
    minimumWorkflowVersion:"1.2.1",
    supportsPublishRequired:true,
    supportsRichWorkflowDefaults:true,
    supportedFieldTypes:clone(FIELD_TYPES),

    registerWorkflow:registerWorkflow,
    registerWorkflows:registerWorkflows,
    unregisterWorkflow:unregisterWorkflow,
    getWorkflow:getWorkflow,
    hasWorkflow:hasWorkflow,
    listWorkflows:listWorkflows,

    productTypeOf:productTypeOf,
    createProductDraft:createProductDraft,
    normalizeProduct:normalizeProduct,
    upgradeProduct:upgradeProduct,

    renderWorkflowChoices:renderWorkflowChoices,
    renderFulfilmentFields:renderFulfilmentFields,
    renderBuyerFields:renderBuyerFields,
    renderField:renderField,

    collectFieldValues:collectFieldValues,
    collectFulfilmentValues:collectFulfilmentValues,
    collectBuyerFields:collectBuyerFields,

    validateFulfilment:validateFulfilment,
    validateBuyerFields:validateBuyerFields,
    validateProduct:validateProduct,
    readiness:readiness,
    applyValidationErrors:applyValidationErrors,

    getWorkflowMeta:function (type) {
      var workflow = registry[normalizeType(type)];
      return workflow ? clone(workflow.meta) : null;
    },
    getOrderWorkflow:function (type) {
      var workflow = registry[normalizeType(type)];
      return workflow ? clone(workflow.orderWorkflow) : null;
    },
    getDefaultBuyerFields:function (type) {
      var workflow = registry[normalizeType(type)];
      return workflow ? clone(workflow.buyerFields) : [];
    },
    getSystemBuyerFields:function () {
      return SYSTEM_BUYER_FIELDS.map(normalizeBuyerField).map(clone);
    },
    getDefaultFulfilment:function (type) {
      var workflow = registry[normalizeType(type)];
      return workflow ? defaultFulfilment(workflow) : {};
    },

    buildProductPayload:buildProductPayload,
    buildOrderSnapshot:buildOrderSnapshot,
    runHook:runHook,

    normalizeType:normalizeType,
    normalizeKey:normalizeKey,
    canonicalOrderField:canonicalOrderField,
    isPersistedFileValue:isPersistedFileValue,
    normalizeField:normalizeField,
    normalizeBuyerField:normalizeBuyerField,
    escapeHTML:escapeHTML,
    clone:clone,
    merge:merge,
    on:on
  };

  Object.defineProperty(api, "workflows", {
    enumerable:true,
    get:function () {
      return listWorkflows({ includeInactive:true });
    }
  });

  window.ClikkerProductEngine = Object.freeze(api);
  console.info("[Clikker Product Engine] Ready", VERSION);
})(window, document);
